import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import API from "../utils/api";

// Manager queue for event (QR) registrations: review, then hand to a rep.
// Assigning turns a registration into a real CRM lead owned by that rep.

const FILTERS = [
  ["new", "New"],
  ["assigned", "Assigned"],
  ["dismissed", "Dismissed"],
  ["test", "Test"],
];

const when = (v) => v
  ? new Date(String(v).replace(" ", "T")).toLocaleString("en-GB", {
      timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true,
    })
  : "";

export default function EventLeads({ accent = "#2AB5A5", onCount }) {
  const [status, setStatus] = useState("new");
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [assignees, setAssignees] = useState([]);
  const [picked, setPicked] = useState([]);
  const [owner, setOwner] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(`/event-leads?status=${status}`);
      setLeads(res.data.leads || []);
      setCounts(res.data.counts || {});
      setPicked([]);
      if (onCount) onCount(res.data.counts?.new || 0);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not load event registrations.");
    } finally {
      setLoading(false);
    }
  }, [status, onCount]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    API.get("/event-leads/assignees").then((r) => setAssignees(r.data || [])).catch(() => {});
  }, []);

  const allIds = leads.map((l) => l.id);
  const allPicked = allIds.length > 0 && picked.length === allIds.length;
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const act = async (path, body, done) => {
    setBusy(true);
    try {
      const res = await API.post(`/event-leads/${path}`, body);
      toast.success(done(res.data));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "That didn't work. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const assign = () => {
    if (!picked.length) return toast.error("Tick at least one registration.");
    if (!owner) return toast.error("Choose who should follow up.");
    act("assign", { ids: picked, user_id: Number(owner) }, (d) =>
      `${d.assigned} assigned to ${d.owner}${d.skipped ? ` (${d.skipped} skipped)` : ""}`);
  };

  const btn = (bg, extra = {}) => ({
    background: bg, color: "#fff", border: 0, borderRadius: 8, padding: "8px 14px",
    fontSize: 12, fontWeight: 800, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, ...extra,
  });

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {FILTERS.map(([key, label]) => (
          <button key={key} onClick={() => setStatus(key)} style={{
            border: `1.5px solid ${status === key ? accent : "#e2e8f0"}`, background: status === key ? accent : "#fff",
            color: status === key ? "#fff" : "#475569", borderRadius: 999, padding: "6px 14px", fontSize: 12, fontWeight: 800, cursor: "pointer",
          }}>{label} · {counts[key] || 0}</button>
        ))}
        <button onClick={load} style={{ marginLeft: "auto", border: "1px solid #e2e8f0", background: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Refresh</button>
      </div>

      {status === "new" && leads.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12, marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 700, display: "flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={allPicked} onChange={() => setPicked(allPicked ? [] : allIds)} /> Select all
          </label>
          <select value={owner} onChange={(e) => setOwner(e.target.value)}
            style={{ flex: "1 1 180px", padding: "8px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}>
            <option value="">Assign to…</option>
            {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}{a.designation ? ` · ${a.designation}` : ""}</option>)}
          </select>
          <button disabled={busy} onClick={assign} style={btn(accent)}>Assign{picked.length ? ` (${picked.length})` : ""}</button>
          <button disabled={busy || !picked.length} onClick={() => act("dismiss", { ids: picked }, (d) => `${d.updated} dismissed`)} style={btn("#94a3b8")}>Dismiss</button>
        </div>
      )}
      {status === "dismissed" && picked.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <button disabled={busy} onClick={() => act("reopen", { ids: picked }, (d) => `${d.updated} moved back to New`)} style={btn(accent)}>Move back to New ({picked.length})</button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>Loading…</div>
      ) : leads.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", background: "#fff", borderRadius: 12, border: "1px dashed #cbd5e1" }}>
          {status === "new" ? "No new registrations yet. They appear here as visitors scan the QR." : "Nothing here."}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {leads.map((l) => {
            const selectable = status === "new" || status === "dismissed";
            return (
              <div key={l.id} style={{ background: "#fff", border: `1px solid ${picked.includes(l.id) ? accent : "#e2e8f0"}`, borderRadius: 12, padding: "12px 14px", display: "flex", gap: 10 }}>
                {selectable && <input type="checkbox" checked={picked.includes(l.id)} onChange={() => toggle(l.id)} style={{ marginTop: 3 }} aria-label={`Select ${l.contact_name}`} />}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
                    <b style={{ fontSize: 14, color: "#1e293b" }}>{l.contact_name}</b>
                    <span style={{ fontSize: 12, color: "#64748b" }}>{l.designation}</span>
                    {l.channel === "stall" && <span style={{ fontSize: 10, fontWeight: 800, background: "#e0f2fe", color: "#0369a1", borderRadius: 6, padding: "1px 6px" }}>STALL</span>}
                    {l.submissions > 1 && <span style={{ fontSize: 10, fontWeight: 800, background: "#fef3c7", color: "#92400e", borderRadius: 6, padding: "1px 6px" }}>SCANNED {l.submissions}×</span>}
                  </div>
                  <div style={{ fontSize: 13, color: "#334155", marginTop: 2 }}>{l.institution_name}{l.city ? `, ${l.city}` : ""}</div>
                  <div style={{ fontSize: 12, marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <a href={`tel:+91${l.phone}`} style={{ color: "#0f766e", fontWeight: 800, textDecoration: "none" }}>+91 {l.phone}</a>
                    {l.email && <a href={`mailto:${l.email}`} style={{ color: "#475569", textDecoration: "none" }}>{l.email}</a>}
                  </div>
                  {l.interest_labels?.length > 0 && (
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                      {l.interest_labels.map((i) => <span key={i} style={{ fontSize: 11, fontWeight: 700, background: "#ecfeff", color: "#0e7490", borderRadius: 999, padding: "2px 9px" }}>{i}</span>)}
                    </div>
                  )}
                  {l.meeting_label && (
                    <div style={{ marginTop: 6, display: "inline-block", fontSize: 12, fontWeight: 800, background: "#fff1f0", color: "#b42318", borderRadius: 8, padding: "3px 9px" }}>
                      Meeting: {l.meeting_label} IST
                    </div>
                  )}
                  {l.student_count ? <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{l.student_count} students</div> : null}
                  {l.notes && <div style={{ fontSize: 12, color: "#475569", marginTop: 4, fontStyle: "italic" }}>“{l.notes}”</div>}
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                    {when(l.created_at)}{l.status === "assigned" && l.assigned_name ? ` · Assigned to ${l.assigned_name}` : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
